import {
  RoutePostCategories_categoryId_Stores_storeId_FixturesPog,
  type ResponseGetCategories_categoryId_Stores_storeId_FixturesPog,
} from "@CommonApi/categories/_categoryId_/stores/_storeId_/fixturesPog";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import type { PsaAisleSplitSuggestion } from "@CommonUtils/psa/aisleSplitGap";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useApi } from "src/api";
import { invalidateCategoryRacks } from "src/api/categoryRacks";
import { Page, Planogram } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import { MerchflowHeader } from "src/components/merchflows/MerchflowHeader/MerchflowHeader";
import { PlanogramToolbar } from "src/components/Planogram/components/Toolbar/PlanogramToolbar";
import { AisleBreakEditingContext } from "src/components/Planogram/store/aisleBreakEditing";
import {
  AisleBreakProvenanceContext,
  readAisleBreakProvenance,
  type AisleBreakProvenanceByBay,
} from "src/components/Planogram/store/aisleBreakProvenance";
import { PlanogramHovererDetails } from "src/components/PlanogramExtensions/PlanogramHovererDetails/PlanogramHovererDetails";
import { PlanogramMetrics } from "src/components/PlanogramExtensions/PlanogramMetrics";
import { Button, Flex } from "src/elements";
import { useModifiedPog } from "src/hooks/useModifiedPogAtom";
import { AisleSplitSuggestions } from "src/pages/PsaRenderer/components/AisleSplitSuggestions";
import { pages } from "src/utils";
import { omniAtom } from "src/utils/atoms";
import { aisleBreaksOn, suggestionsNotBrokenOn, withAisleBreak } from "src/utils/aisleBreaks";
import { sleep } from "src/utils/sleep";

const pogAtom = omniAtom({
  prefix: pages.categories_categoryId_stores_storeId_Fixtures,
  key: "pog",
  value: null as null | Pog,
});

export const PageCategories_categoryId_Stores_storeId_Fixtures = () => {
  const { categoryId: _categoryId, storeId: _storeId } = useParams<{
    categoryId: string;
    storeId: string;
  }>();
  const categoryId = Number(_categoryId);
  const storeId = Number(_storeId);

  const { pog, setPog, revertPog, isModified, savePogAsOriginal, patchPog } =
    useModifiedPog(pogAtom);
  const [aisleBreakSuggestions, setAisleBreakSuggestions] = useState<PsaAisleSplitSuggestion[]>([]);
  const {
    getCategories_categoryId_Stores_storeId_FixturesPogApi,
    postCategories_categoryId_Stores_storeId_FixturesPogApi,
  } = useApi();
  const { hasLoader } = useLoaders();
  const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);
  const queryClient = useQueryClient();
  const [aisleBreakProvenance, setAisleBreakProvenance] = useState<AisleBreakProvenanceByBay>(
    () => new Map(),
  );
  const isSaveLoading = hasLoader(RoutePostCategories_categoryId_Stores_storeId_FixturesPog);
  // The pog on screen when a save comes back, to tell whether it was edited meanwhile.
  const latestPogRef = useRef(pog);
  useEffect(() => {
    latestPogRef.current = pog;
  }, [pog]);
  const isModifiedRef = useRef(isModified);
  isModifiedRef.current = isModified;
  // A fixtures answer that lands after navigation belongs to another store; adopting it would
  // let Save write that layout here.
  const routeRef = useRef({ categoryId, storeId });
  routeRef.current = { categoryId, storeId };
  const isStillOn = (forCategoryId: number, forStoreId: number) =>
    routeRef.current.categoryId === forCategoryId && routeRef.current.storeId === forStoreId;

  useEffect(() => {
    loadFixturesPog(categoryId, storeId);
  }, [categoryId, storeId]);

  useEffect(() => {
    return () => {
      setPog(null);
    };
  }, []);

  // The GET and the POST both answer with the stored fixtures: their bay ids name the stored
  // sections, so the next save keeps the writer of every break it only kept.
  const adoptStoredFixtures = (
    fixtures: ResponseGetCategories_categoryId_Stores_storeId_FixturesPog,
  ) => {
    setPog(fixtures);
    setAisleBreakProvenance(readAisleBreakProvenance(fixtures));
    // A backend from before the GET carried suggestions sends none.
    setAisleBreakSuggestions(fixtures.aisle_break_suggestions ?? []);
  };

  const loadFixturesPog = async (categoryId: number, storeId: number) => {
    const response = await getCategories_categoryId_Stores_storeId_FixturesPogApi({
      categoryId: String(categoryId),
      storeId: String(storeId),
    });
    if (response && isStillOn(categoryId, storeId)) {
      adoptStoredFixtures(response);
    }
  };

  // The declare rewrote the stored fixtures, so the bay ids on screen are stale: reload them and
  // the new break's writer, unless that would discard an unsaved edit.
  const onAisleBreakDeclared = async (afterBayNo: number) => {
    patchPog(withAisleBreak(afterBayNo));
    if (isModifiedRef.current) return;
    const response = await getCategories_categoryId_Stores_storeId_FixturesPogApi({
      categoryId: String(categoryId),
      storeId: String(storeId),
    });
    if (response && !isModifiedRef.current && isStillOn(categoryId, storeId)) {
      adoptStoredFixtures(response);
    }
  };

  const onSave = async () => {
    if (!pog) return;
    const savedPog = pog;

    const response = await postCategories_categoryId_Stores_storeId_FixturesPogApi(
      { categoryId: String(categoryId), storeId: String(storeId) },
      savedPog.toNonCircular(),
    );

    if (response !== null) {
      void invalidateCategoryRacks(queryClient, categoryId);
      setIsSaveSuccessful(true);
      await sleep(1000);
      // Not the pre-save pog: the saved bays carry fresh ids, and new breaks their writer. An
      // edit made while saving stays on screen as unsaved instead (as does everything, on a
      // backend from before the POST answered with the saved fixtures).
      if (isStillOn(categoryId, storeId)) {
        if (response?.planogram && latestPogRef.current === savedPog) adoptStoredFixtures(response);
        else savePogAsOriginal(savedPog.toNonCircular());
      }
      setIsSaveSuccessful(false);
    }
  };

  return (
    <Page>
      <MerchflowHeader categoryId={categoryId} storeId={storeId} />

      <PlanogramToolbar pogAtom={pogAtom} showProducts={false} showCanvasRenderer />

      <AisleSplitSuggestions
        key={`${categoryId}-${storeId}`}
        suggestions={suggestionsNotBrokenOn(aisleBreakSuggestions, pog)}
        // Numbered against the stored fixtures, so only while the screen shows them unedited.
        target={isModified ? undefined : { categoryId, storeId }}
        onDeclared={onAisleBreakDeclared}
        breaksAfterBayNos={aisleBreaksOn(pog)}
      />

      <Flex minHeight="0" flexGrow column>
        <AisleBreakEditingContext.Provider value>
          <AisleBreakProvenanceContext.Provider value={aisleBreakProvenance}>
            <Planogram
              pogAtom={pogAtom}
              isEditable
              isPanelVisible
              isPog
              isWithBorder
              showPanelProducts={false}
              allowKonvaEditor
            />
          </AisleBreakProvenanceContext.Provider>
        </AisleBreakEditingContext.Provider>
      </Flex>

      <PlanogramHovererDetails />

      <Flex justify="between" align="center">
        <PlanogramMetrics pogAtom={pogAtom} />

        {isModified && (
          <Flex gap="10px">
            <Button
              color="red"
              variant="inverted"
              isDisabled={isSaveLoading || isSaveSuccessful}
              onClick={revertPog}
            >
              Revert Changes
            </Button>

            <Button isLoading={isSaveLoading} isSuccessful={isSaveSuccessful} onClick={onSave}>
              Save
            </Button>
          </Flex>
        )}
      </Flex>
    </Page>
  );
};
